import React, { useState, useRef, useEffect, useCallback } from "react";
import { useMedia } from "../../contexts/MediaContext";

const ResizableEditArea = ({
  children,
  className = "",
  initialWidth = "60%",
  initialHeight = "45dvh",
  minWidth = 300,
  minHeight = 200,
  maxWidth = "90%",
  maxHeight = "80dvh",
  disableResize = false,
}) => {
  // MediaContextから直接isMediaOpenを取得（props依存を排除）
  // MediaProviderが存在しない場合はfalseとして扱う
  let isMediaOpen = false;
  let hasMediaContext = false;
  let saveResizeWidth = null;
  let restoreResizeWidth = null;

  try {
    const mediaContext = useMedia();
    if (mediaContext) {
      isMediaOpen = mediaContext.isMediaOpen || false;
      hasMediaContext = true;
      saveResizeWidth = mediaContext.saveResizeWidth;
      restoreResizeWidth = mediaContext.restoreResizeWidth;
    }
  } catch (error) {
    // MediaProviderが存在しない場合（LessonEditPage等）はfalseとして扱う
    console.warn(
      "MediaContext not available in ResizableEditArea - this is normal for pages without MediaProvider"
    );
  }
  const [size, setSize] = useState({
    width: initialWidth,
    height: initialHeight,
  });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const containerRef = useRef(null);
  const startPos = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const animationFrameRef = useRef(null);
  const throttleTimeoutRef = useRef(null);

  // 初期サイズをピクセル値に変換
  useEffect(() => {
    if (!containerRef.current) return;

    if (typeof initialWidth === "string" && initialWidth.includes("%")) {
      const widthPx = (window.innerWidth * parseInt(initialWidth)) / 100;
      setSize((prev) => ({ ...prev, width: `${widthPx}px` }));
    }
    if (typeof initialHeight === "string" && initialHeight.includes("dvh")) {
      const heightPx = (window.innerHeight * parseInt(initialHeight)) / 100;
      setSize((prev) => ({ ...prev, height: `${heightPx}px` }));
    }
  }, [initialWidth, initialHeight]);

  // デバイス判定
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // リサイズ開始
  const handleResizeStart = (e, direction) => {
    if (disableResize) return;

    e.preventDefault();
    e.stopPropagation();

    setIsResizing(true);
    setResizeDirection(direction);

    const rect = containerRef.current.getBoundingClientRect();
    startPos.current = {
      x: e.clientX,
      y: e.clientY,
      width: rect.width,
      height: rect.height,
    };

    document.body.style.userSelect = "none";
    document.body.style.cursor =
      direction === "horizontal" ? "ew-resize" : "ns-resize";
  };

  // 最大幅・高さを事前計算（パフォーマンス向上）
  const maxWidthPx =
    typeof maxWidth === "string" && maxWidth.includes("%")
      ? (window.innerWidth * parseInt(maxWidth)) / 100
      : parseInt(maxWidth);
  const maxHeightPx =
    typeof maxHeight === "string" && maxHeight.includes("dvh")
      ? (window.innerHeight * parseInt(maxHeight)) / 100
      : parseInt(maxHeight);

  // リサイズ処理（requestAnimationFrame + throttle最適化）
  const performResize = useCallback(
    (clientX, clientY) => {
      if (!containerRef.current) return;

      const deltaX = clientX - startPos.current.x;
      const deltaY = clientY - startPos.current.y;

      if (resizeDirection === "horizontal") {
        const newWidth = Math.max(minWidth, startPos.current.width + deltaX);
        const finalWidth = Math.min(newWidth, maxWidthPx);

        // GPU最適化のためtransform使用を検討、ただし互換性のためwidthも残す
        containerRef.current.style.width = `${finalWidth}px`;
        // containerRef.current.style.transform = `scaleX(${finalWidth / startPos.current.width})`;
      } else if (resizeDirection === "vertical") {
        const newHeight = Math.max(minHeight, startPos.current.height + deltaY);
        const finalHeight = Math.min(newHeight, maxHeightPx);
        containerRef.current.style.height = `${finalHeight}px`;
      }
    },
    [resizeDirection, minWidth, minHeight, maxWidthPx, maxHeightPx]
  );

  // throttle機能付きリサイズハンドラー
  const handleResizeMove = useCallback(
    (e) => {
      if (!isResizing) return;

      e.preventDefault();

      // requestAnimationFrameで既にスケジュール済みの場合はスキップ
      if (animationFrameRef.current) return;

      animationFrameRef.current = requestAnimationFrame(() => {
        performResize(e.clientX, e.clientY);
        animationFrameRef.current = null;
      });
    },
    [isResizing, performResize]
  );

  // リサイズ終了
  const handleResizeEnd = useCallback(() => {
    // アニメーションフレームをクリーンアップ
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (!containerRef.current) return;

    // 最終的なサイズをstateに保存
    const rect = containerRef.current.getBoundingClientRect();
    if (resizeDirection === "horizontal" && !isMobile) {
      const newWidth = `${rect.width}px`;
      setSize((prev) => ({ ...prev, width: newWidth }));

      // リサイズ後の横幅をMediaContextに保存
      if (saveResizeWidth) {
        saveResizeWidth(newWidth);
      }
    } else if (resizeDirection === "vertical" && isMobile) {
      setSize((prev) => ({ ...prev, height: `${rect.height}px` }));
    }

    setIsResizing(false);
    setResizeDirection(null);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, [resizeDirection, isMobile, saveResizeWidth]);

  // MediaGalleryの開閉状態を監視し、閉じる時に横幅を復元
  useEffect(() => {
    if (!hasMediaContext) return;

    if (!isMediaOpen && restoreResizeWidth) {
      const savedWidth = restoreResizeWidth();
      if (savedWidth && containerRef.current) {
        setSize((prev) => ({ ...prev, width: savedWidth }));
      }
    }
  }, [isMediaOpen, hasMediaContext, restoreResizeWidth]);

  // グローバルイベントリスナー（最適化）
  useEffect(() => {
    if (!isResizing) return;

    // passiveオプションで最適化、ただしpreventDefaultが必要な場合はfalse
    const moveOptions = { passive: false, capture: true };
    const upOptions = { passive: true, capture: true };

    document.addEventListener("mousemove", handleResizeMove, moveOptions);
    document.addEventListener("mouseup", handleResizeEnd, upOptions);

    return () => {
      document.removeEventListener("mousemove", handleResizeMove, moveOptions);
      document.removeEventListener("mouseup", handleResizeEnd, upOptions);
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  return (
    <div
      className="resizable-editarea-container"
      style={isMobile ? { justifyItems: "center", display: "grid" } : undefined}
    >
      <div
        ref={containerRef}
        className={`editarea ${isMediaOpen ? "media" : ""} ${className}`}
        style={(() => {
          const baseStyles = {
            // GPU アクセラレーション最適化
            willChange: isResizing ? "width, height" : "auto",
            transform: "translateZ(0)", // ハードウェアアクセラレーション強制
            backfaceVisibility: "hidden", // 描画最適化
          };

          const styles = isMediaOpen
            ? {
                ...baseStyles,
                // メディア開いている時はCSSに完全に委ねる
                height: isMobile ? "100%" : "90vh",
                maxHeight: isMobile ? "90%" : "90dvh",
                position: isMobile ? "fixed" : "relative",
                resize: "none",
              }
            : {
                ...baseStyles,
                // 通常時は保存された横幅を優先、なければデフォルト値を使用
                width: isMobile
                  ? "98%"
                  : className.includes("schedule")
                  ? "98%"
                  : size.width || "60%",
                maxWidth: isMobile
                  ? "none"
                  : className.includes("schedule")
                  ? "98%"
                  : "800px",
                height: isMobile ? size.height : "auto",
                maxHeight: isMobile ? size.height : "90dvh",
                ...(isMobile ? {} : { position: "relative" }),
                resize: "none",
              };
          return styles;
        })()}
      >
        {React.isValidElement(children)
          ? React.cloneElement(children, {
              onResizeStart: disableResize ? undefined : handleResizeStart,
            })
          : typeof children === "function"
          ? children({
              onResizeStart: disableResize ? undefined : handleResizeStart,
            })
          : children}
      </div>

      {/* リサイズ中のオーバーレイ */}
      {isResizing && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            cursor:
              resizeDirection === "horizontal" ? "ew-resize" : "ns-resize",
          }}
        />
      )}
    </div>
  );
};

export default ResizableEditArea;
